"use client"

import { useState, useEffect } from "react"
import "./NewCategoryManager.css"
import newsCategoryService from "../../../services/api/newsCategoryService"

export default function NewCategoryManager() {
    const [categories, setCategories] = useState([])
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        photo: "/placeholder.svg?height=100&width=300"
    })
    const [showAddForm, setShowAddForm] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [showDetail, setShowDetail] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    // Fetch categories on component mount
    useEffect(() => {
        fetchCategories()
    }, [])

    // Fetch all categories
    const fetchCategories = async () => {
        try {
            setLoading(true)
            const response = await newsCategoryService.getAllCategories()
            console.log('Fetched categories:', response)
            setCategories(Array.isArray(response.categories) ? response.categories : [])
        } catch (error) {
            console.error("Error fetching categories:", error)
            setError("Không thể tải danh sách danh mục tin tức")
        } finally {
            setLoading(false)
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData({ ...formData, [name]: value })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setFormData({
                        ...formData,
                        photo: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            const response = await newsCategoryService.createCategory(formData)
            console.log('Created category:', response)
            await fetchCategories()
            setFormData({
                name: "",
                description: "",
                photo: "/placeholder.svg?height=100&width=300"
            })
            setShowAddForm(false)
        } catch (error) {
            console.error("Error creating category:", error)
            setError("Không thể tạo danh mục tin tức mới")
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa danh mục này không?")) {
            try {
                setLoading(true)
                const response = await newsCategoryService.deleteCategory(id)
                console.log('Deleted category:', response)
                await fetchCategories()
                if (selectedCategory && selectedCategory._id === id) {
                    setShowDetail(false)
                    setSelectedCategory(null)
                }
            } catch (error) {
                console.error("Error deleting category:", error)
                setError("Không thể xóa danh mục tin tức")
            } finally {
                setLoading(false)
            }
        }
    }

    const handleViewDetails = async (category) => {
        try {
            setLoading(true)
            console.log('Fetching details for category:', category)
            const response = await newsCategoryService.getCategoryById(category._id)
            console.log('Received category details:', response)
            setSelectedCategory(response.category)
            setShowDetail(true)
        } catch (error) {
            console.error("Error fetching category details:", error)
            setError("Không thể tải thông tin chi tiết danh mục")
        } finally {
            setLoading(false)
        }
    }

    const toggleAddForm = () => setShowAddForm(!showAddForm)

    return (
        <div className="ncm-container">
            {!showDetail ? (
                <>
                    <h1 className="ncm-heading">Quản Lý Danh Mục Tin Tức</h1>
                    {error && <div className="error-message">{error}</div>}
                    {loading && <div className="loading-spinner">Đang tải...</div>}
                    <div className="ncm-layout">
                        <div className="ncm-list-container">
                            <div className="ncm-list-header">
                                <h2 className="ncm-subheading">Danh Sách Danh Mục Tin Tức</h2>
                                <button className="ncm-add-button" onClick={toggleAddForm} disabled={loading}>
                                    {showAddForm ? "Ẩn Form" : "Thêm Danh Mục Mới"}
                                </button>
                            </div>
                            <div className="ncm-list">
                                {Array.isArray(categories) && categories.length > 0 ? (
                                    categories.map((category) => (
                                        <div key={category._id} className="ncm-card">
                                            <div className="ncm-image">
                                                <img src={category.photo || "/placeholder.svg"} alt={`Ảnh ${category.name}`} />
                                            </div>
                                            <div className="ncm-details">
                                                <h3>{category.name}</h3>
                                                <p><strong>Mô tả:</strong> {category.description}</p>
                                                <div className="ncm-actions">
                                                    <button className="ncm-view-button" onClick={() => handleViewDetails(category)} disabled={loading}>
                                                        Xem Chi Tiết
                                                    </button>
                                                    <button className="ncm-delete-button" onClick={() => handleDelete(category._id)} disabled={loading}>
                                                        Xóa
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-categories">Không tìm thấy danh mục nào</div>
                                )}
                            </div>
                        </div>

                        {showAddForm && (
                            <div className="ncm-add-section">
                                <div className="ncm-add-card">
                                    <h2 className="ncm-subheading">Thêm Danh Mục Mới</h2>
                                    <form onSubmit={handleSubmit} className="ncm-form">
                                        <div className="ncm-form-group">
                                            <label htmlFor="name">Tên Danh Mục</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>

                                        <div className="ncm-form-group">
                                            <label htmlFor="description">Mô tả</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                rows="4"
                                                required
                                            />
                                        </div>

                                        <div className="ncm-form-group">
                                            <label htmlFor="photo">Ảnh Danh Mục</label>
                                            <input
                                                type="file"
                                                id="photo"
                                                name="photo"
                                                onChange={handleImageChange}
                                                accept="image/*"
                                            />
                                            <div className="ncm-image-preview">
                                                <img src={formData.photo} alt="Xem trước ảnh danh mục" />
                                            </div>
                                        </div>

                                        <div className="ncm-form-actions">
                                            <button type="button" className="ncm-cancel-button" onClick={toggleAddForm}>
                                                Hủy
                                            </button>
                                            <button type="submit" className="ncm-submit-button" disabled={loading}>
                                                {loading ? "Đang thêm..." : "Thêm Danh Mục"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="ncm-detail-section">
                    <button className="ncm-back-button" onClick={() => setShowDetail(false)} disabled={loading}>Quay Lại</button>
                    <div className="ncm-detail-content">
                        <div className="ncm-detail-image">
                            <img src={selectedCategory.photo || "/placeholder.svg"} alt={`Ảnh ${selectedCategory.name}`} />
                        </div>
                        <div className="ncm-detail-info">
                            <h2>{selectedCategory.name}</h2>
                            <p><strong>Mô tả:</strong> {selectedCategory.description}</p>
                            <button className="ncm-delete-button" onClick={() => { handleDelete(selectedCategory._id); setShowDetail(false); }} disabled={loading}>
                                Xóa
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
} 